import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    IconButton,
    Stack,
    Typography,
    Box,
    Button,
    Link,
    CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import { collectiveTracking } from 'modules/common/components/AnalyticsTracking/utils';
import useReferralPromoConfig from 'modules/common/hooks/useReferralPromoConfig';
import useMembershipInfo from 'services/member/queries/useMembershipInfo';
import FriendReferral from 'assets/img/funnel/friend-referral.webp';
import referralGroupImage from 'assets/illustrations/referralGroup.png';
import ChevronRightIcon from 'modules/common/components/Icons/ChevronRightIcon';
import useImpactEmbedAvailability from 'modules/common/integrations/Impact/useImpactEmbedAvailability';

interface ReferralModalProps {
    open: boolean;
    onClose: () => void;
    jwtToken?: string | null;
    jwtError?: string | null;
}

function ReferralModal({ open, onClose, jwtToken = null, jwtError = null }: ReferralModalProps) {
    const [linkCopied, setLinkCopied] = useState(false);
    const { calculateRewardBalance } = useReferralPromoConfig();

    // Mounted closed in the header on every page, so only fetch once the member opens it.
    const {
        data: membershipInfo,
        isLoading: isMembershipInfoLoading,
        failureCount: membershipInfoFailureCount,
    } = useMembershipInfo({ enabled: open });
    const hasJwt = !!jwtToken && !jwtError;
    const { data: isEmbedAvailable } = useImpactEmbedAvailability({ enabled: open && hasJwt });
    // Falls back to the plain copy-link layout when a content or network filter blocks the widget.
    const showEmbed = hasJwt && isEmbedAvailable !== false;

    const referralCode = membershipInfo?.referral?.code;
    const referralLink = referralCode
        ? `https://share.collective.com/${referralCode}`
        : 'https://www.collective.com/referral/';

    const copyLinkText = 'Copy link';

    useEffect(() => {
        if (open) {
            collectiveTracking.track({
                name: 'Referral Modal Viewed',
                properties: {
                    category: 'engagement',
                    action: 'view',
                },
            });
        }
    }, [open]);

    const handleCloseModal = () => {
        collectiveTracking.track({
            name: 'Refer Modal Dismissed',
            properties: {
                category: 'engagement',
                action: 'click',
            },
        });
        setLinkCopied(false);
        onClose();
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(referralLink).then(() => {
            setLinkCopied(true);
            collectiveTracking.track({
                name: 'Referral Link Copied',
                properties: {
                    category: 'engagement',
                    action: 'click',
                    referral_link_url: referralLink,
                    cta_text: copyLinkText,
                },
            });
            setTimeout(() => setLinkCopied(false), 3000);
        });
    };

    return (
        <Dialog
            open={open}
            onClose={handleCloseModal}
            fullWidth
            maxWidth={false}
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    maxWidth: '480px !important',
                    width: '100%',
                },
            }}>
            <IconButton
                aria-label="close"
                onClick={handleCloseModal}
                sx={{
                    position: 'absolute',
                    right: 16,
                    top: 16,
                    color: 'cement.700',
                    bgcolor: 'cement.100',
                    width: 36,
                    height: 36,
                    transition: 'all 0.1s ease-in-out',
                    '&:hover': {
                        bgcolor: 'grey.200',
                    },
                }}>
                <CloseIcon fontSize="small" />
            </IconButton>

            <DialogContent sx={{ p: { xs: 3, sm: 4 }, pt: '1rem !important' }}>
                {showEmbed ? (
                    <Stack alignItems="center" sx={{ pt: 2 }}>
                        <Box sx={{ width: '60%', maxWidth: 150, mb: 1 }}>
                            <img src={referralGroupImage} alt="Referral Group" style={{ width: '100%' }} />
                        </Box>

                        <Typography variant="h1" component="h4" sx={{ lineHeight: 1.2 }}>
                            Give 1 month free &
                        </Typography>
                        <Typography variant="h1" component="h4" sx={{ lineHeight: 1.2 }}>
                            earn {calculateRewardBalance(1)}
                        </Typography>

                        <Typography variant="subtitle1" color="text.secondary" sx={{ my: 1 }}>
                            Earn cash for every referral{' '}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 4 }}>
                            <Link
                                href="/referrals"
                                color="inherit"
                                underline="none"
                                sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                <Typography variant="subtitle1" color="inherit">
                                    Learn more
                                </Typography>
                                <ChevronRightIcon sx={{ fontSize: 16, ml: 0.5 }} />
                            </Link>
                        </Typography>

                        <Box
                            sx={{
                                display: 'flex',
                                width: '100%',
                                border: '1px solid',
                                borderColor: 'grey.100',
                                borderRadius: 3,
                                overflow: 'hidden',
                                mb: 4,
                                '& > *': {
                                    flex: '1 1 0%',
                                    minWidth: 0,
                                },
                            }}>
                            <Box
                                sx={{
                                    borderRight: '1px solid',
                                    borderColor: 'grey.100',
                                    py: 2,
                                    px: 3,
                                }}>
                                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 0.5 }}>
                                    1 friend
                                </Typography>
                                <Typography variant="h3" component="p" color="success.main">
                                    +{calculateRewardBalance(1)}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    borderRight: '1px solid',
                                    borderColor: 'grey.100',
                                    py: 2,
                                    px: 3,
                                }}>
                                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 0.5 }}>
                                    2 friends
                                </Typography>
                                <Typography variant="h3" component="p" color="success.main">
                                    +{calculateRewardBalance(2)}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    py: 2,
                                    px: 3,
                                }}>
                                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 0.5 }}>
                                    5 friends
                                </Typography>
                                <Typography variant="h3" component="p" color="success.main">
                                    +{calculateRewardBalance(5)}
                                </Typography>
                            </Box>
                        </Box>

                        <Typography variant="h4" component="p" sx={{ textAlign: 'left', width: '100%' }}>
                            Share your referral code
                        </Typography>
                        <Box sx={{ width: '100%', my: 2, height: 120 }}>
                            <impact-embed widget="w/dashboard-social-share-full-width">
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        minHeight: 120,
                                        width: '100%',
                                    }}>
                                    <CircularProgress size={24} />
                                </Box>
                            </impact-embed>
                        </Box>
                    </Stack>
                ) : (
                    <Stack spacing={0} alignItems="center">
                        {/* Friend referral image */}
                        <Box sx={{ width: '60%', maxWidth: 150, mb: 1 }}>
                            <img src={FriendReferral} alt="Friend Referral" style={{ width: '100%' }} />
                        </Box>

                        {/* Main title */}
                        <Box sx={{ mb: 0 }}>
                            <Typography variant="h1" component="h4" sx={{ lineHeight: 1.2 }}>
                                Give 1 month free &<br />
                                earn {calculateRewardBalance(1)}
                            </Typography>
                        </Box>

                        {/* Subtitle */}
                        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4, mt: 2 }}>
                            Earn cash for every referral{' '}
                        </Typography>

                        {/* Referral tiers */}
                        <Box
                            sx={{
                                display: 'flex',
                                width: '100%',
                                border: '1px solid',
                                borderColor: 'grey.100',
                                borderRadius: 3,
                                overflow: 'hidden',
                                mb: 2,
                            }}>
                            <Box
                                sx={{
                                    flex: 1,
                                    p: 2,
                                    borderRight: '1px solid',
                                    borderColor: 'grey.100',
                                    textAlign: 'center',
                                }}>
                                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 0.5 }}>
                                    1 friend
                                </Typography>
                                <Typography variant="h3" component="p" color="success.main">
                                    +{calculateRewardBalance(1)}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    flex: 1,
                                    p: 2,
                                    borderRight: '1px solid',
                                    borderColor: 'grey.100',
                                    textAlign: 'center',
                                }}>
                                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 0.5 }}>
                                    2 friends
                                </Typography>
                                <Typography variant="h3" component="p" color="success.main">
                                    +{calculateRewardBalance(2)}
                                </Typography>
                            </Box>
                            <Box sx={{ flex: 1, p: 2, textAlign: 'center' }}>
                                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 0.5 }}>
                                    5 friends
                                </Typography>
                                <Typography variant="h3" component="p" color="success.main">
                                    +{calculateRewardBalance(5)}
                                </Typography>
                            </Box>
                        </Box>

                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            // After a failed attempt, let the member copy the generic link rather than wait out retries.
                            disabled={isMembershipInfoLoading && membershipInfoFailureCount === 0}
                            onClick={handleCopyLink}
                            sx={{
                                backgroundColor: linkCopied ? 'success.main' : 'purple.main',
                                '&:hover': {
                                    backgroundColor: linkCopied ? 'success.dark' : 'purple.dark',
                                },
                                py: 1.5,
                                mt: 1,
                            }}>
                            {linkCopied ? 'Copied!' : copyLinkText}
                        </Button>
                    </Stack>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default ReferralModal;
